import { capitalize } from '@mui/material';
import { MdChevronRight, MdMonitor } from '@teamworksdev/icons';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '../../../../trpc/react';
import { BlockPreview, BlockPublishedWorkoutLayer } from '../types';

interface DisplayedWorkoutBadgeProps {
  block: BlockPreview;
  blockPublishedWorkoutLayers: BlockPublishedWorkoutLayer[];
  onClick: () => void;
}

/**
 * Mirrors display.getSelection directly rather than keeping independent
 * state - it's the single source of truth for the header badge (see
 * fetchDisplaySelectionForTeam's own comment on the server side). Renders
 * nothing when the team has no active selection, or when that selection's
 * workout is not a current published layer on this block (displays only
 * resolve published content).
 */
export const DisplayedWorkoutBadge = ({
  block,
  blockPublishedWorkoutLayers,
  onClick,
}: DisplayedWorkoutBadgeProps) => {
  const { t } = useTranslation();
  const { data: selection } = trpc.display.getSelection.useQuery({
    teamId: block.teamId,
  });

  const currentPublishedLayers = useMemo(
    () => blockPublishedWorkoutLayers.filter((l) => l.isCurrent),
    [blockPublishedWorkoutLayers],
  );

  const displayedLayer = selection
    ? currentPublishedLayers.find((l) => l.workoutId === selection.workoutId)
    : undefined;

  if (!displayedLayer) return null;

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-1 rounded-full bg-green-100/50 px-3 py-1.5 text-xs font-medium text-success-700"
    >
      <MdMonitor size={16} aria-hidden="true" />
      <span>
        {t('displayedWorkoutBadgeLabel', {
          cycleLabel: capitalize(block.cycleLabel),
          cycleNumber: displayedLayer.cycleSortOrder,
          sessionLabel: capitalize(block.sessionLabel),
          sessionNumber: displayedLayer.sessionSortOrder,
        })}
      </span>
      <MdChevronRight size={16} aria-hidden="true" />
    </button>
  );
};
